import React, { useState, useMemo } from 'react';
import { EXTERNAL_URLS } from '../constants';

/**
 * Displays a station's logo/image using a 3-tier fallback chain:
 *  1. favicon URL from Radio Browser API (direct logo)
 *  2. Google favicon service using the station's homepage domain
 *     → gives the real company logo for any station with a website
 *  3. Coloured initials avatar generated from the station name
 */

const PALETTE = [
  '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
  '#1565C0', '#00838F', '#2E7D32', '#E65100',
  '#BF360C', '#4E342E', '#37474F', '#6A1B9A',
  '#0277BD', '#00695C', '#558B2F', '#F9A825',
];

function avatarColor(name) {
  if (!name) return PALETTE[0];
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length];
}

function shortName(name) {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  const first = words[0];
  return first.length > 6 ? first.slice(0, 6).toUpperCase() : first.toUpperCase();
}

/** Extract the bare domain from a URL, e.g. "https://www.91.9fm.in/..." → "91.9fm.in" */
function domainFromUrl(url) {
  if (!url) return null;
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/** Build a Google S2 favicon URL for a given domain at 128 px. */
function googleFaviconUrl(domain) {
  if (!domain) return null;
  return `${EXTERNAL_URLS.GOOGLE_FAVICON}?domain=${encodeURIComponent(domain)}&sz=128`;
}

const StationAvatar = ({ name = '', favicon, homepage, size = 44, playing = false, className = '' }) => {
  const [faviconFailed, setFaviconFailed] = useState(false);
  const [googleFailed, setGoogleFailed] = useState(false);

  /* Build the Google-favicon URL once from the homepage */
  const googleUrl = useMemo(() => {
    const domain = domainFromUrl(homepage);
    return googleFaviconUrl(domain);
  }, [homepage]);

  const containerStyle = {
    width: size,
    height: size,
    borderRadius: 8,
    flexShrink: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  /* Playing state — animated equalizer bars */
  if (playing) {
    return (
      <div
        className={`station-avatar station-avatar--playing ${className}`}
        style={{ ...containerStyle, background: 'var(--accent-dim)', border: '1px solid var(--accent)' }}
        aria-label="Now playing"
      >
        <div className="station-playing-indicator">
          <span /><span /><span />
        </div>
      </div>
    );
  }

  const imgStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  /* Tier 1 — favicon URL from Radio Browser */
  if (favicon && !faviconFailed) {
    return (
      <div className={`station-avatar ${className}`} style={containerStyle}>
        <img
          src={favicon}
          alt={name}
          style={imgStyle}
          onError={() => setFaviconFailed(true)}
        />
      </div>
    );
  }

  /* Tier 2 — Google favicon service (uses the station's homepage domain) */
  if (googleUrl && !googleFailed) {
    return (
      <div className={`station-avatar ${className}`} style={containerStyle}>
        <img
          src={googleUrl}
          alt={name}
          style={imgStyle}
          onError={() => setGoogleFailed(true)}
        />
      </div>
    );
  }

  /* Tier 3 — Coloured name avatar */
  const bg = avatarColor(name);
  const label = shortName(name);
  const fontSize = size <= 38 ? 9 : size <= 50 ? 10 : 12;

  return (
    <div
      className={`station-avatar station-avatar--initials ${className}`}
      style={{ ...containerStyle, background: bg, padding: 2 }}
      aria-label={name}
    >
      <span style={{
        color: '#fff',
        fontSize,
        fontWeight: 700,
        letterSpacing: '0.03em',
        lineHeight: 1.2,
        userSelect: 'none',
        textAlign: 'center',
        wordBreak: 'break-all',
      }}>
        {label}
      </span>
    </div>
  );
};

export default StationAvatar;
